import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex } from 'reflexbox/styled-components'
import debounce from 'lodash/debounce'
import FlexSearch from 'flexsearch'
import { Button } from 'react-scroll'

import IconSearch from '@anvilco-pkg/ui/components/Icon/IconSearch'

import theme from 'theme'
import media from 'media'

const StyledContainer = styled(Flex).attrs({
  flexWrap: 'wrap',
  width: [1],
  className: 'anvil-search-bar',
})`
  position: relative;
`

export const SearchContainer = styled(Flex).attrs({
  alignItems: 'center',
})`
  width: 100%;
  border-bottom: 2px solid ${theme.colors.inputBorderColorActive} !important;
`

const getSearchBoxAttrs = ({ placeholder }) => ({
  placeholder: placeholder || 'Search',
  fontSize: theme.fontSizes[80],
  fontFamily: theme.fontFamilies.body,
})
const StyledSearchBox = styled.input.attrs(getSearchBoxAttrs)`
  position: relative;
  height: 40px;
  width: 100%;
  display: flex;
  flex: 1;
  color: black;
  padding-left: 10px;
  border: none;
  outline: none;
`

const EnterButton = styled(Button).attrs({
  ariaHidden: 'true',
  smooth: true,
  type: 'hidden',
})`
  position: absolute;
  background: none !important;
  box-shadow: none !important;
  z-index: 100;
  margin-top: 5px;
  margin-left: 250px;

  &:hover {
    background: none;
  }

  &:active,
  &:focus {
    position: absolute;
    background: none;
  }

  ${media.tablet`
    position: absolute;
    margin-top: 5px;
    margin-left: 100%;
  `}
`

// could memoize results - e.g. memoize search query --> results
// not really a big case to do this yet though
function SearchBar ({
  query,
  onKeyEnter,
  onSearch,
  placeholder,
  scroll,
  localSearchData,
  searchOptions,
  ...others
}) {
  const [value, setValue] = useState('')

  const index = null
  const enableEnterButton = onKeyEnter || scroll

  useEffect(() => {
    document.querySelector('.search-box').addEventListener('keyup', handleEnter)

    return () => {
      document
        .querySelector('.search-box')
        ?.removeEventListener('keyup', handleEnter)
    }
  }, [])

  useEffect(() => {
    setValue(query)
  }, [query])

  useEffect(() => {
    search(value)
  }, [value])

  const handleEnter = (e) => {
    // on enter release
    if (e.keyCode === 13 && enableEnterButton) {
      e.preventDefault()
      document.querySelector('.search-enter-button').click()
    }
  }

  const getOrImportIndex = () => {
    if (index) {
      return index
    }

    if (localSearchData?.index) {
      const importedIndex = FlexSearch.create()
      importedIndex.import(localSearchData.index)

      return importedIndex
    }
  }

  const performFlexSearch = (searchQuery) => {
    const index = getOrImportIndex()

    if (index) {
      // search has lots of cool options. on of them is enabling suggestions:
      // https://github.com/nextapps-de/flexsearch/tree/0.6.32#suggestions
      const rawResults = index.search({
        query: searchQuery,
        suggest: searchOptions?.suggest,
        bool: searchOptions?.bool || 'and',
        fields: searchOptions?.fields,
      })

      return rawResults.map((id) => localSearchData.store[id])
    }
  }

  const search = useCallback(
    debounce((searchQuery) => {
      let results = performFlexSearch(searchQuery, index)
      if (searchOptions?.idsOnly) {
        results = results?.map((result) => result[searchOptions.idFieldName])
      }
      onSearch({
        query: searchQuery,
        results,
        searchOptions,
      })
    }, 500),
    []
  )

  const onChange = (evt) => {
    setValue(evt.target.value)
  }

  return (
    <StyledContainer data-cy="search-bar" {...others}>
      {enableEnterButton && (
        <EnterButton
          className="button search-enter-button"
          to={scroll?.name}
          offset={scroll?.offset}
          onClick={onKeyEnter}
          name="enter-button"
        />
      )}
      <SearchContainer>
        <IconSearch fill={theme.colors.black} />
        <StyledSearchBox
          placeholder={placeholder}
          className="search-box"
          type="text"
          value={value}
          onChange={onChange}
        />
      </SearchContainer>
    </StyledContainer>
  )
}

export const searchProps = {
  placeholder: PropTypes.string,
  localSearchData: PropTypes.shape({
    index: PropTypes.string.isRequired,
    store: PropTypes.object.isRequired,
  }).isRequired,
  query: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
}

SearchBar.propTypes = {
  onKeyEnter: PropTypes.func,
  scroll: PropTypes.shape({
    name: PropTypes.string.isRequired,
    offset: PropTypes.number,
  }),
  searchOptions: PropTypes.object,
  ...searchProps,
}

export default SearchBar
